/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        this.closest('.video-section').addEventListener('click', (evt) => {
          if (evt.target.closest('[data-video-motion-toggle]')) return;
          this.play();
        });
      }
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay && !this.shouldHoldAutoplay(),
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');
        this.setupMotionControl();

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
          this.syncMotionControl(false);
        });

        this.player.on('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
          this.syncMotionControl(true);
          this.dispatchEvent(new CustomEvent('on:media:play', {
            detail: {
              player: this.iframe
            },
            bubbles: true
          }));
        });

        if (this.shouldHoldAutoplay()) {
          this.pausedByUser = true;
          this.syncMotionControl(false);
        } else {
          this.play();
        }
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');
      this.applyMediaLabel(this.player);
      this.setupMotionControl();
      this.setupDescriptionAnnouncer();

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
        this.syncMotionControl(false);
      });

      this.player.addEventListener('play', () => {
        this.syncMotionControl(true);
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.player
          },
          bubbles: true
        }));
      });

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        this.closest('.video-section').classList.add('video-section--played');
        this.syncMotionControl(true);
      } else {
        this.player.addEventListener('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.addObserver(this.player);

        // Mute the video if a click didn't trigger its load
        const deferredMedia = this.closest('deferred-media');
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
        }

        if (this.shouldHoldAutoplay()) {
          this.pausedByUser = true;
          this.player.removeAttribute('autoplay');
          this.player.pause();
          this.syncMotionControl(false);
        } else {
          this.play();
        }
      }
    }

    /**
     * Focus/hover-gated description announcements for silent background videos.
     * Keeps TextTrack mode "hidden" (no visual overlay) and only mirrors active cues
     * into an aria-live region while the shopper is interacting with the video area.
     *
     * Shopify Files often serves .vtt as application/octet-stream, which makes the
     * native <track> fail (readyState ERROR, 0 cues). We still keep <track> in HTML
     * for scanners, then fetch + parse cues into an in-memory TextTrack.
     */
    setupDescriptionAnnouncer() {
      if (!this.background || this.dataset.descriptions !== 'true' || !this.player) return;

      const root = this.closest('[data-video-desc-root]')
        || this.closest('.video-section');
      const section = this.closest('.video-section');
      const liveRegion = section?.querySelector('[data-video-desc-live]');
      if (!root || !liveRegion) return;

      this.descAnnounceActive = false;
      this.descLiveRegion = liveRegion;
      this.descRoot = root;

      const trackEl = this.player.querySelector('track[kind="descriptions"]');
      if (!trackEl?.src) return;

      this.loadDescriptionCues(trackEl.src).then((track) => {
        if (!track) return;
        this.bindDescriptionTrack(track, root);
      });
    }

    /**
     * Fetches WebVTT and builds a hidden descriptions TextTrack with VTTCue entries.
     * @param {string} src - CDN URL of the .vtt file.
     * @returns {Promise<TextTrack|null>}
     */
    async loadDescriptionCues(src) {
      try {
        const response = await fetch(src, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) return null;

        const vttText = await response.text();
        const cues = this.parseVttCues(vttText);
        if (!cues.length) return null;

        // Disable the native <track> — Shopify Files MIME often makes it ERROR with 0 cues.
        const nativeTrackEl = this.player.querySelector('track[kind="descriptions"]');
        if (nativeTrackEl?.track) nativeTrackEl.track.mode = 'disabled';

        // Prefer an in-memory track so we are not blocked by native <track> MIME errors.
        const track = this.player.addTextTrack('descriptions', 'Descriptions', 'en');
        track.mode = 'hidden';
        cues.forEach(({ start, end, text }) => {
          try {
            track.addCue(new VTTCue(start, end, text));
          } catch (err) {
            // Older engines may throw on overlapping cues; skip bad rows.
          }
        });

        return track;
      } catch (err) {
        console.warn('Unable to load video descriptions VTT', err); // eslint-disable-line no-console
        return null;
      }
    }

    /**
     * Minimal WebVTT cue parser (timing lines + text blocks).
     * @param {string} vttText - Raw VTT file contents.
     * @returns {{start: number, end: number, text: string}[]}
     */
    parseVttCues(vttText) {
      const cues = [];
      const blocks = String(vttText || '')
        .replace(/^\uFEFF/, '')
        .split(/\r?\n\r?\n+/);

      blocks.forEach((block) => {
        const lines = block.split(/\r?\n/).filter((line) => line.trim() !== '');
        if (!lines.length) return;

        let timingIndex = lines.findIndex((line) => line.includes('-->'));
        if (timingIndex === -1) return;

        const timing = lines[timingIndex];
        const match = timing.match(
          /(\d{1,2}:)?(\d{1,2}):(\d{1,2})\.(\d{3})\s*-->\s*(\d{1,2}:)?(\d{1,2}):(\d{1,2})\.(\d{3})/
        );
        if (!match) return;

        const start = this.vttTimestampToSeconds(match[1], match[2], match[3], match[4]);
        const end = this.vttTimestampToSeconds(match[5], match[6], match[7], match[8]);
        const text = lines.slice(timingIndex + 1).join(' ').trim();
        if (!text || !(end > start)) return;

        cues.push({ start, end, text });
      });

      return cues;
    }

    /**
     * @param {string|undefined} hoursPart - Optional "HH:" capture.
     * @param {string} minutes
     * @param {string} seconds
     * @param {string} ms
     * @returns {number}
     */
    vttTimestampToSeconds(hoursPart, minutes, seconds, ms) {
      const hours = hoursPart ? parseInt(hoursPart.replace(':', ''), 10) : 0;
      return (hours * 3600)
        + (parseInt(minutes, 10) * 60)
        + parseInt(seconds, 10)
        + (parseInt(ms, 10) / 1000);
    }

    /**
     * Wires focus/hover gating and cuechange → aria-live for a descriptions track.
     * @param {TextTrack} track
     * @param {Element} root - Hero container used for focus/hover detection.
     */
    bindDescriptionTrack(track, root) {
      if (!track || this.descTrackBound) return;
      this.descTrackBound = true;
      this.descTrack = track;
      track.mode = 'hidden';

      const activate = () => {
        this.descAnnounceActive = true;
        this.announceActiveDescriptionCue(track);
      };

      const deactivate = () => {
        this.descAnnounceActive = false;
        this.clearDescriptionAnnouncement();
      };

      root.addEventListener('focusin', activate);
      root.addEventListener('focusout', (evt) => {
        if (!root.contains(evt.relatedTarget)) deactivate();
      });
      root.addEventListener('mouseenter', activate);
      root.addEventListener('mouseleave', deactivate);

      track.addEventListener('cuechange', () => {
        this.announceActiveDescriptionCue(track);
      });

      // If the shopper is already hovering/focused when cues finish loading.
      if (root.matches(':hover') || root.contains(document.activeElement)) {
        activate();
      }
    }

    /**
     * Finds the descriptions TextTrack (from <track kind="descriptions">).
     * @returns {TextTrack|null}
     */
    getDescriptionTextTrack() {
      const tracks = this.player.textTracks;
      if (!tracks) return null;

      for (let i = 0; i < tracks.length; i += 1) {
        if (tracks[i].kind === 'descriptions') return tracks[i];
      }

      const trackEl = this.player.querySelector('track[kind="descriptions"]');
      return trackEl?.track || null;
    }

    /**
     * Pushes the active description cue into the live region when interaction is active.
     * @param {TextTrack} track - Descriptions text track.
     */
    announceActiveDescriptionCue(track) {
      if (!this.descLiveRegion) return;

      if (!this.descAnnounceActive) {
        this.clearDescriptionAnnouncement();
        return;
      }

      const activeCues = track.activeCues;
      const nextText = activeCues && activeCues.length
        ? String(activeCues[0].text || '').trim()
        : '';

      if (!nextText) {
        this.clearDescriptionAnnouncement();
        return;
      }

      // Skip identical text so focusin + mouseenter (or repeated cuechange) does not
      // cause NVDA to speak the same cue twice.
      if (nextText === this.descLiveRegion.textContent) return;
      this.descLiveRegion.textContent = nextText;
    }

    /**
     * Clears the live region so background description chatter stops immediately.
     */
    clearDescriptionAnnouncement() {
      if (this.descLiveRegion) this.descLiveRegion.textContent = '';
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');
      this.setupMotionControl();

      if (this.autoplay) this.player.mute();

      if (this.shouldHoldAutoplay()) {
        this.pausedByUser = true;
        this.syncMotionControl(false);
      } else {
        this.play();
      }
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
        this.syncMotionControl(false);
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.closest('.video-section').classList.add('video-section--played');
        this.syncMotionControl(true);
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.iframe
          },
          bubbles: true
        }));
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.classList.add(jsClass);
      this.applyMediaLabel(this.iframe);
      this.addObserver(this.iframe);
    }

    /**
     * Applies an accessible name, or hides decorative background media from AT.
     * @param {HTMLElement} el - Video or iframe element.
     */
    applyMediaLabel(el) {
      if (!el) return;

      const description = (this.dataset.description || '').trim();

      if (description) {
        if (el.tagName === 'IFRAME') el.title = description;
        else el.setAttribute('aria-label', description);
        el.removeAttribute('aria-hidden');
      } else if (this.background) {
        el.setAttribute('aria-hidden', 'true');
        if (el.tagName === 'IFRAME') el.title = '';
      } else if (el.tagName === 'IFRAME') {
        el.title = description;
      }
    }

    /**
     * Whether autoplay should be withheld (background + reduced motion).
     * @returns {boolean}
     */
    shouldHoldAutoplay() {
      return this.background && this.prefersReducedMotion;
    }

    /**
     * Wires the pause/play control for background videos (WCAG 2.2.2).
     */
    setupMotionControl() {
      if (!this.background) return;

      const section = this.closest('.video-section');
      const btn = section?.querySelector('[data-video-motion-toggle]');
      if (!btn || btn.dataset.bound === 'true') return;

      this.motionBtn = btn;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        this.toggleMotion();
      });
    }

    /**
     * Toggles background video playback from the motion control.
     */
    toggleMotion() {
      if (this.pausedByUser || !this.motionPlaying) {
        this.pausedByUser = false;
        this.play();
        this.syncMotionControl(true);
      } else {
        this.pause(true);
        this.syncMotionControl(false);
      }
    }

    /**
     * Syncs motion control label/icons with playing state.
     * @param {boolean} playing - Whether the video is playing.
     */
    syncMotionControl(playing) {
      const section = this.closest('.video-section');
      const btn = this.motionBtn || section?.querySelector('[data-video-motion-toggle]');
      if (!btn) return;

      this.motionPlaying = playing;
      this.motionBtn = btn;

      const pauseIcon = btn.querySelector('.video-section__motion-btn-icon--pause');
      const playIcon = btn.querySelector('.video-section__motion-btn-icon--play');

      btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
      btn.setAttribute('aria-label', playing
        ? (btn.dataset.labelPause || 'Pause video')
        : (btn.dataset.labelPlay || 'Play video'));
      section.classList.toggle('video-section--motion-paused', !playing);

      if (pauseIcon) pauseIcon.hidden = !playing;
      if (playIcon) playIcon.hidden = playing;
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
      } else {
        this.player.play();
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     * @param {boolean} [userInitiated=false] - Whether the user requested the pause.
     */
    pause(userInitiated = false) {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = userInitiated;
        } else if (userInitiated) {
          this.pausedByUser = true;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) this.player.pause();
          this.pausedByUser = userInitiated;
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = userInitiated;
      } else if (userInitiated) {
        this.pausedByUser = true;
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        }, { rootMargin: '0px 0px 200px 0px' });
      });

      observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}
